import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useProgramPanel } from 'contexts/programPanelContext';
import { useTranslation } from 'react-i18next';
import { statisticsApi } from 'api/statistics';
import { surveyApi } from 'api/survey';
import { Spinner } from 'components/_new/Spinner';
import { Box, BoxSection } from 'components/_new/Box';
import { StatisticsProgramFeedbackItem } from 'types/Statistics';
import { SurveyQuestionType } from 'types/Survey';
import { useAuth } from 'contexts/authContext';
import { Controller, useForm } from 'react-hook-form';
import { Dropdown } from 'components/_new/Dropdown';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { Button } from 'components/_new/Button';
import { Dialog } from 'components/_new/Dialog';
import { classNames } from 'primereact/utils';
import { Divider } from 'components/_new/Divider';
import { useGlobal } from 'contexts/globalContext';
import { StatsItemProgress } from '../programStatistics/components/StatsItem';

type FeedbackAnswerItemProps = {
	type:
		| SurveyQuestionType.LONGTEXT
		| SurveyQuestionType.MULTI_CHOICE
		| SurveyQuestionType.SINGLE_CHOICE
		| SurveyQuestionType.TEXT;
	text: string;
	value: number | string[] | null;
	choices: null | Array<{ id: number; text: string; value: number }>;
	className?: string;
	disabled?: boolean;
};

const FeedbackAnswerItem = ({ type, text, value, choices, className, disabled }: FeedbackAnswerItemProps) => {
	const { t } = useTranslation();
	const [showModal, setShowModal] = useState(false);

	const isText = [SurveyQuestionType.TEXT, SurveyQuestionType.LONGTEXT].includes(type);
	const isChoice = [SurveyQuestionType.MULTI_CHOICE, SurveyQuestionType.SINGLE_CHOICE].includes(type);
	const answersCount = useMemo(() => {
		if (isChoice) {
			return value as number;
		}
		return Array.isArray(value) ? (value as string[]).length : 0;
	}, [isChoice, value]);
	const answers = Array.isArray(value) ? (value as string[]) : [];

	return (
		<>
			<Box
				variant="white-bordered"
				slim
				className={classNames('stats-item', disabled ? 'stats-item-disabled' : 'stats-item-rose', className)}
			>
				<BoxSection>
					<div className="font-semibold text-lg">{text}</div>
				</BoxSection>
				<BoxSection horizontal contentClassName="justify-content-between gap-2">
					{(isText || isChoice) && (
						<>
							<div className="flex flex-row gap-3 justify-items-center align-items-center">
								<div className="perc">
									{t('programPanel.feedback.answers', {
										count: answersCount || 0,
									})}
								</div>
							</div>

							<Button
								label={t('programPanel.feedback.showAnswers')}
								onClick={() => setShowModal(true)}
								variant="primary-outlined"
								variantSize="sm"
								disabled={!answersCount}
							/>
						</>
					)}
				</BoxSection>
			</Box>
			<Dialog title={text} visible={showModal} onHide={() => setShowModal(false)}>
				{answers &&
					answers.map((str, idx) => (
						// eslint-disable-next-line react/no-array-index-key
						<div key={idx} className="p-2 border-bottom-1 surface-border">
							{str}
						</div>
					))}
				{isChoice &&
					(choices || []).map(({ id, text: choiceText, value: choiceValue }, index) => (
						<div key={id} className="flex justify-content-between p-2 border-bottom-1 surface-border">
							<div>
								<span className="inline-block" style={{ minWidth: 20 }}>
									{index + 1}
									{'. '}
								</span>
								<span className="inline-block">{choiceText}</span>
							</div>
							<span className="text-light-purplish-blue font-bold">
								{choiceValue !== null ? `${choiceValue}%` : t('misc.noData')}
							</span>
						</div>
					))}
			</Dialog>
		</>
	);
};

type FeedbackItemGroup = Array<{
	step: number;
	items: Array<Omit<StatisticsProgramFeedbackItem, 'step'>>;
}>;

type UserTypeFilter = 'all' | 'mentors' | 'mentees';

type FilterValues = {
	userType: UserTypeFilter;
};

export const ProgramFeedback = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toastRef } = useGlobal();

	const { currentUser } = useAuth();

	const { data: program } = useProgramPanel();

	const { control, watch } = useForm<FilterValues>({
		defaultValues: {
			userType: 'all',
		},
	});
	const selectedUserType = watch('userType');
	const apiUserType: 'mentors' | 'mentees' | undefined = selectedUserType === 'all' ? undefined : selectedUserType;
	const feedbackQueryKey = [
		'programStatistics',
		{
			type: 'feedback',
			programId: Number(program?.id),
			language: currentUser?.language,
			userType: selectedUserType,
		},
	] as const;

	const { data: feedbackData, isLoading: menteeIsLoading } = useQuery(
		feedbackQueryKey,
		() => statisticsApi.getFeedback(Number(program?.id), apiUserType),
		{
			enabled: Boolean(program),
			cacheTime: 10000,
		}
	);

	const { mutate: downloadXlsx, isLoading: downloadXlsxLoading } = useMutation(
		() => surveyApi.panel.downloadFeedbackStatisticsXlsx(Number(program?.id)),
		{
			onSuccess: (blob) => {
				const downloadLink = document.createElement('a');
				downloadLink.rel = 'noreferrer';
				downloadLink.target = '_blank';
				downloadLink.download = 'feedback.xlsx';
				const downloadUrl = URL.createObjectURL(blob);
				downloadLink.href = downloadUrl;
				document.body.append(downloadLink);
				downloadLink.click();
				setTimeout(() => {
					downloadLink.remove();
				}, 100);
			},
		}
	);

	const handleDownload = useCallback(() => {
		downloadXlsx();
	}, [downloadXlsx]);

	const { mutate: refreshFeedbackStats, isLoading: refreshFeedbackStatsLoading } = useMutation(
		() => statisticsApi.refreshFeedbackStats(Number(program?.id), apiUserType),
		{
			onSuccess: (data) => {
				queryClient.setQueryData(feedbackQueryKey, data);
				toastRef.current?.show({
					severity: 'success',
					life: 2500,
					summary: t('misc.success'),
					detail: 'Statystyki odświeżone',
				});
			},
		}
	);

	const handleRefresh = useCallback(() => {
		refreshFeedbackStats();
	}, [refreshFeedbackStats]);

	if (menteeIsLoading || !feedbackData) {
		return <Spinner />;
	}

	const data = feedbackData.answerStats
		.filter((item) => {
			const hasNoAnswers =
				item.value === null ||
				item.value === undefined ||
				(Array.isArray(item.value) && item.value.length === 0) ||
				([SurveyQuestionType.SINGLE_CHOICE, SurveyQuestionType.MULTI_CHOICE].includes(item.type) &&
					item.value === 0);
			return !(item.excluded && hasNoAnswers);
		})
		.reduce<FeedbackItemGroup>((current, { step, ...item }) => {
			const idx = current.findIndex((ctg) => ctg.step === step);
			if (idx === -1) {
				current.push({
					step,
					items: [item],
				});
			} else {
				current[idx].items.push(item);
			}
			return current;
		}, [])
		.filter((ctg) => ctg.items.length > 0);

	return (
		<>
			<SubPageTitle
				title={t('organizationPanel.programFeedback')}
				additional={program?.displayName}
				right={() => (
					<div className="flex flex-column gap-2 md:flex-row align-items-center justify-content-end">
						<Controller
							control={control}
							name="userType"
							render={({ field }) => (
								<Dropdown
									{...field}
									inline
									options={[
										{ label: t('programPanel.statistics.filter.all'), value: 'all' },
										{
											label: t('programPanel.statistics.filter.mentorsOnly'),
											value: 'mentors',
										},
										{
											label: t('programPanel.statistics.filter.menteesOnly'),
											value: 'mentees',
										},
									]}
								/>
							)}
						/>
						<Button
							className="p-button-sm min-w-max max-h-3rem"
							label={t('misc.downloadXls')}
							loading={downloadXlsxLoading}
							onClick={handleDownload}
						/>
						<Button
							className="p-button-sm min-w-max max-h-3rem"
							label="Odśwież"
							icon="refresh"
							iconSet="pi"
							loading={refreshFeedbackStatsLoading}
							onClick={handleRefresh}
						/>
						<Button
							className=" p-button p-button-sm min-w-max max-h-3rem"
							label={t('programPanel.feedback.questionManagement')}
							onClick={() => navigate(`/program/${program?.name}/feedback/edit-questions`)}
						/>
					</div>
				)}
			/>

			<p>{t('organizationPanel.feedbackDesc', { count: feedbackData.count })}</p>

			{data.map(({ step, items }, index) => (
				<>
					<div className="grid">
						{items.map(({ type, text, value, positivePerc, choices, excluded }, idx) => {
							if (type === SurveyQuestionType.INFO) {
								return null;
							}
							if (type === SurveyQuestionType.SCALE) {
								return (
									<div className="col-12 lg:col-6">
										<StatsItemProgress
											// eslint-disable-next-line react/no-array-index-key
											key={`${step}-${idx}`}
											name={text}
											value={`${value || 0}/10`}
											progressValue={Number(value) * 10}
											positivePercValue={positivePerc !== null ? Number(positivePerc) : undefined}
											variant="purplish"
											className=" h-full"
											disabled={excluded}
										/>
									</div>
								);
							}
							if (type === SurveyQuestionType.BOOLEAN) {
								return (
									<div className="col-12 lg:col-6">
										<StatsItemProgress
											// eslint-disable-next-line react/no-array-index-key
											key={`${step}-${idx}`}
											name={text}
											value={`${value ?? 0}%`}
											progressValue={Number(value)}
											variant="purplish"
											className=" h-full"
											disabled={excluded}
										/>
									</div>
								);
							}
							return (
								<div className="col-12 lg:col-6">
									<FeedbackAnswerItem
										// eslint-disable-next-line react/no-array-index-key
										key={`${step}-${idx}`}
										type={type}
										text={text}
										value={value as any}
										choices={choices}
										className=" h-full"
										disabled={excluded}
									/>
								</div>
							);
						})}
					</div>
					{data.length - 1 !== index ? (
						<div className="pt-4 pb-5">
							<Divider />
						</div>
					) : null}
				</>
			))}
		</>
	);
};
